import { useMemo, useState } from 'preact/hooks';
import type { Currency } from '../../shared/types';
import { fmtPct } from '../../shared/format';
import { useMask } from '../lib/mask';
import './../styles/components/donut.css';

export type DonutSlice = {
  id: string;
  label: string;
  value: number;
  color: string;
};

type Props = {
  data: DonutSlice[];
  ccy: Currency;
  size?: number;
  ariaLabel?: string;
};

const VB = 100;
const CX = 50;
const CY = 50;
const R_OUTER = 40;
const R_INNER = 28;
const GAP_DEG = 1.2;

function polar(angleDeg: number, r: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(a), y: CY + r * Math.sin(a) };
}

function arcPath(startDeg: number, endDeg: number): string {
  const a0 = Math.min(startDeg, endDeg);
  const a1 = Math.max(startDeg, endDeg);
  const largeArc = a1 - a0 > 180 ? 1 : 0;
  const so = polar(a0, R_OUTER);
  const eo = polar(a1, R_OUTER);
  const si = polar(a1, R_INNER);
  const ei = polar(a0, R_INNER);
  return [
    `M ${so.x} ${so.y}`,
    `A ${R_OUTER} ${R_OUTER} 0 ${largeArc} 1 ${eo.x} ${eo.y}`,
    `L ${si.x} ${si.y}`,
    `A ${R_INNER} ${R_INNER} 0 ${largeArc} 0 ${ei.x} ${ei.y}`,
    'Z',
  ].join(' ');
}

export function Donut({ data, ccy, size = 220, ariaLabel }: Props) {
  const [hover, setHover] = useState<string | null>(null);
  const { amount } = useMask();
  const total = data.reduce((s, d) => s + d.value, 0);

  const slices = useMemo(() => {
    let angle = 0;
    return data
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((d) => {
        const share = total > 0 ? d.value / total : 0;
        const sweep = share * 360;
        const start = angle + GAP_DEG / 2;
        const end = angle + sweep - GAP_DEG / 2;
        angle += sweep;
        return { ...d, share, start, end, mid: (start + end) / 2 };
      });
  }, [data, total]);

  const activeSlice = hover ? slices.find((s) => s.id === hover) : null;
  const centerValue = activeSlice ? activeSlice.value : total;
  const centerLabel = activeSlice ? activeSlice.label : 'Total';
  const centerShare = activeSlice ? activeSlice.share : 1;

  return (
    <div class="donut" style={`--donut-size: ${size}px`}>
      <div class="donut-chart">
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          role="img"
          aria-label={ariaLabel}
          onMouseLeave={() => setHover(null)}
        >
          {slices.map((s) => (
            <path
              key={s.id}
              class={`donut-slice ${hover && hover !== s.id ? 'is-dim' : ''} ${hover === s.id ? 'is-active' : ''}`}
              d={arcPath(s.start, s.end)}
              style={`fill: ${s.color}`}
              onMouseEnter={() => setHover(s.id)}
              onFocus={() => setHover(s.id)}
              onBlur={() => setHover(null)}
              tabIndex={0}
              aria-label={`${s.label}: ${amount(s.value, ccy)} (${fmtPct(s.share)})`}
            />
          ))}
        </svg>
        <div class="donut-center">
          <div class="donut-center-label mono">{centerLabel}</div>
          <div class="donut-center-value mono">{amount(centerValue, ccy)}</div>
          <div class="donut-center-pct mono">{fmtPct(centerShare)}</div>
        </div>
      </div>
      <ul class="donut-legend">
        {slices.map((s) => (
          <li
            class={`donut-legend-row ${hover && hover !== s.id ? 'is-dim' : ''} ${hover === s.id ? 'is-active' : ''}`}
            key={s.id}
            onMouseEnter={() => setHover(s.id)}
            onMouseLeave={() => setHover(null)}
          >
            <span class="donut-swatch" style={`background: ${s.color}`} />
            <span class="donut-legend-label">{s.label}</span>
            <span class="donut-legend-pct mono">{fmtPct(s.share)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
